import {useFocusManagement} from '@aboutyou/a11y';
import {useOpenedStateManager} from '@aboutyou/opened-state-manager';
import {Fade} from '@aboutyou/transitions';
import React, {
  FC,
  MouseEvent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled, {css} from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {Z_INDEXES} from '../../../constants/layers';
import {useKeyPress} from '../../../hooks/useKeyPress/useKeyPress';
import {useLockBodyScroll} from '../../../hooks/useLockBodyScroll/useLockBodyScroll';

export const STICKY_BAR_OPENED_STATE_MANAGER_DEFAULT_KEY = 'sticky-bar';
const ANIMATE_ON_ENTRY_DURATION_TIME = 750;

interface StickyBarProps {
  className?: string;
  onClick?: VoidFunction;
  ['data-testid']?: string;
  BodyContent: ({onClose}: {onClose: VoidFunction}) => React.ReactNode;
  headerBackgroundColor?: string;
  openedStateManagerKey?: string;
  enableInfiniteInitialBounceAnimation?: boolean;
  increaseBorderRadius?: boolean;
  disableHeaderHoverEffect?: boolean;
  OpenerContent: ReactNode;
  /**
   * Guestimate the height of the content based on the text and icon
   * this is important to set the height of the opener tab
   */
  closeStateMaxHeight: string;
}

export const StickyBar: FC<StickyBarProps> = ({
  className,
  onClick,
  'data-testid': dataTestId = 'stickyBar',
  BodyContent,
  headerBackgroundColor,
  openedStateManagerKey = STICKY_BAR_OPENED_STATE_MANAGER_DEFAULT_KEY,
  enableInfiniteInitialBounceAnimation,
  increaseBorderRadius,
  disableHeaderHoverEffect,
  OpenerContent,
  closeStateMaxHeight,
}) => {
  const [isAnimationActive, setIsAnimationActive] = useState(false);
  const {isOpen, open, hide} = useOpenedStateManager(openedStateManagerKey);
  useLockBodyScroll({lockScroll: isOpen});
  const hasBeenOpenedOnce = useRef(isOpen);

  const triggerAnimation = () => {
    setIsAnimationActive(true);

    setTimeout(() => {
      setIsAnimationActive(false);
    }, 2100);
  };

  const onCloseHandler = () => {
    hide({withNewHistoryEntry: true});
    triggerAnimation();
  };

  useEffect(() => {
    triggerAnimation();
  }, []);

  const onClickHandler = (e: MouseEvent) => {
    e.preventDefault();
    if (isOpen) {
      hide();
      triggerAnimation();
    } else {
      open({replaceHistory: true});
    }

    if (!hasBeenOpenedOnce.current) {
      hasBeenOpenedOnce.current = true;
    }

    onClick?.();
  };

  const getAnimationPlayState = () => {
    if (enableInfiniteInitialBounceAnimation) {
      return !hasBeenOpenedOnce.current;
    }
    return isAnimationActive;
  };

  const {focusManagementRefCallback, isTopMostPopover} = useFocusManagement({
    isOpen,
    priority: 1,
  });

  useKeyPress({
    key: 'Escape',
    onKeyDown: onCloseHandler,
    disabled: isTopMostPopover === false,
  });

  return (
    <PositionElement className={className} data-is-open={isOpen}>
      <Fade
        mounted={isOpen}
        duration={
          enableInfiniteInitialBounceAnimation
            ? ANIMATE_ON_ENTRY_DURATION_TIME
            : 200
        }
      >
        <Overlay
          data-testid={`${dataTestId}Overlay`}
          onClick={onClickHandler}
        />
      </Fade>

      <Wrapper
        $infiniteBounceAnimation={enableInfiniteInitialBounceAnimation}
        $closeStateMaxHeight={closeStateMaxHeight}
        data-testid={dataTestId}
        ref={focusManagementRefCallback()}
      >
        <OpenerTab
          $backgroundColor={headerBackgroundColor}
          data-testid={`${dataTestId}Header`}
          onClick={onClickHandler}
          data-activate-animation={getAnimationPlayState()}
          $infiniteBounceAnimation={enableInfiniteInitialBounceAnimation}
          $increaseBorderRadius={increaseBorderRadius}
        >
          {!disableHeaderHoverEffect && <HeaderOverlay />}

          {OpenerContent}
        </OpenerTab>
        <BodyContentWrapper data-testid={`${dataTestId}ContentContainer`}>
          {BodyContent({onClose: onCloseHandler})}
        </BodyContentWrapper>
      </Wrapper>
    </PositionElement>
  );
};

const MIN_HEIGHT = 200;
const OPENER_TAB_MAX_WIDTH = 40;
const BOUNCE_TRANSLATE_X_ADJUSTMENT = 3;
const BOUNCE_TRANSITION_TIMING_FUNCTION = 'cubic-bezier(0.45, 0.44, 0, 1.13)';

const BodyContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  overflow: auto;
`;

const PositionElement = styled.div`
  right: 0;
  top: 0;
  bottom: 0;
  position: fixed;
  width: 0;
  display: flex;
  align-items: center;
  z-index: ${Z_INDEXES.GENERIC_FLYOUT};
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  opacity: 0.6;
  background: rgba(0, 0, 0, 0.6);
  z-index: ${Z_INDEXES.GENERIC_FLYOUT};

  [data-is-open='false'] & {
    pointer-events: none;
  }
`;

const HeaderOverlay = styled.div`
  background-color: ${COLORS.Black};
  opacity: 0;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;

  &:hover {
    opacity: 0.1;
  }
`;

const OpenerTab = styled.button<{
  $backgroundColor?: string;
  $infiniteBounceAnimation?: boolean;
  $increaseBorderRadius?: boolean;
}>`
  min-width: ${OPENER_TAB_MAX_WIDTH}px;
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
  display: flex;
  text-orientation: inherit;
  writing-mode: vertical-lr;
  justify-content: space-between;
  transform: rotate(-180deg);
  align-items: center;
  text-align: center;
  cursor: pointer;

  ${({$backgroundColor}) =>
    $backgroundColor &&
    css`
      background-color: ${$backgroundColor};
    `};

  ${({$increaseBorderRadius}) =>
    $increaseBorderRadius &&
    css`
      border-top-right-radius: 16px;
      border-bottom-right-radius: 16px;
    `}

  ${({$infiniteBounceAnimation}) =>
    $infiniteBounceAnimation
      ? css`
          animation: infiniteBounce 880ms ease infinite paused;
        `
      : css`
          animation: bounce 2s ease infinite paused;
        `};

  &[data-activate-animation='true'] {
    animation-play-state: running;
  }

  @keyframes bounce {
    0%,
    20%,
    50%,
    80%,
    100% {
      transform: translateX(0);
      rotate: -180deg;
    }
    40% {
      transform: translateX(-${BOUNCE_TRANSLATE_X_ADJUSTMENT * 2}px);
    }
    60% {
      transform: translateX(-${BOUNCE_TRANSLATE_X_ADJUSTMENT}px);
    }
  }

  @keyframes infiniteBounce {
    0%,
    100% {
      transform: translateX(0);
      rotate: -180deg;
    }
    50%,
    60% {
      transform: translateX(-${BOUNCE_TRANSLATE_X_ADJUSTMENT}px);
    }
  }
`;

const Wrapper = styled.div<{
  $infiniteBounceAnimation?: boolean;
  $closeStateMaxHeight: string;
}>`
  position: absolute;
  display: flex;
  min-height: ${MIN_HEIGHT}px;
  z-index: ${Z_INDEXES.GENERIC_FLYOUT};
  left: -${OPENER_TAB_MAX_WIDTH - BOUNCE_TRANSLATE_X_ADJUSTMENT}px;
  will-change: transform, max-height;

  // using translation here => in most cases user will have two discount when they see this
  // which is minimal change if there will be any (+/- 20px)
  // eslint-disable-next-line local-rules/css-layout-animation
  transition: transform 0.4s ease-in-out, max-height 0.4s ease-in-out;

  ${({$closeStateMaxHeight}) => css`
    max-height: ${$closeStateMaxHeight};

    [data-is-open='true'] & {
      transform: translateX(
        calc(-100% + ${OPENER_TAB_MAX_WIDTH - BOUNCE_TRANSLATE_X_ADJUSTMENT}px)
      );
      max-height: clamp(${MIN_HEIGHT}px, calc(100% - 40px), 700px);

      ${BodyContentWrapper} > div {
        transform: translate(0, 0);
      }
    }
  `}

  ${({$infiniteBounceAnimation}) =>
    $infiniteBounceAnimation &&
    css`
      transition-duration: ${ANIMATE_ON_ENTRY_DURATION_TIME}ms;
      transition-timing-function: ${BOUNCE_TRANSITION_TIMING_FUNCTION};
    `}
`;
